<template>
  <div class="image-slider">

    <!-- Main Display: Image or Video -->
    <div class="image-slider__main-display">
      <!-- Image Display -->
      <img
        v-if="selectedItem.type === 'image'"
        :src="selectedItem.large"
        alt="Main Image"
        class="image-slider__main-image"
      />

      <!-- Video Display -->
      <div v-else-if="selectedItem.type === 'video'" class="image-slider__main-video">
        <iframe
          :src="`https://www.youtube.com/embed/${selectedItem.id}?autoplay=1`"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          class="image-slider__video-iframe"
        ></iframe>
      </div>
    </div>

    <!-- Thumbnail Gallery with Navigation -->
    <div ref="thumbnailContainer" class="image-slider__thumbnail-wrap">

      <!-- Nav button left -->
      <button
        class="image-slider__nav-button image-slider__nav-button--left"
        tabindex=0
        @click="thumbnailPrevious"
      >
        <ChevronIcon />
      </button>

      <!-- Items -->
      <flickity ref="flickityThumbnail" :options="optionsThumbnail">
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="['image-slider__thumbnail-cell', { 'active-item': item === selectedItem }]"
        >
          <button
          @click="onThumbnailClick(item)"
          class="image-slider__thumbnail-button">

            <!-- Thumbnail Image -->
            <img
              class="image-slider__thumbnail-image"
              :src=itemThumbnailUrl(item)
            />

            <!-- Play Icon for Videos -->
            <span v-if="item.type === 'video'" class="image-slider__thumbnail-icon">
              <PlayIcon />
            </span>
          </button>

        </div>
      </flickity>

      <!-- Nav button right -->
      <button
        class="image-slider__nav-button image-slider__nav-button--right"
        tabindex=0
        @click="thumbnailNext"
      >
        <ChevronIcon />
      </button>
    </div>
  </div>
</template>
  
<script>
  import Flickity from "vue-flickity";
  import ChevronIcon from "./icons/ChevronIcon.vue";
  import PlayIcon from "./icons/PlayIcon.vue";
  
  export default {
    components: {
      Flickity,
      ChevronIcon,
      PlayIcon,
    },
    props: {
      items: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        selectedItem: this.items[0], // Default to the first item
        optionsThumbnail: {
          pageDots: false,
          prevNextButtons: false, // Use custom buttons
          contain: true,
          groupCells: true,
        },
      }; 
    },
    methods: {

      // Handle thumbnail click:
      // // Update main display and scroll to thumbnails
      async onThumbnailClick(item) {
        // Update the main display first
        this.updateMainDisplay(item);

        // Wait for the DOM to render the updated content
        // otherwise image will be rendered before the scroll.
        this.$nextTick(() => {
          this.waitForImageLoad();
        });
      },

      // Wait for the big image to load before scrolling
      waitForImageLoad() {
        const img = new Image();
        img.src = this.selectedItem.large;

        // Scroll only after image fully loads.
        img.onload = () => {
          this.scrollToThumbnails();
        };
      },

      // Scroll thumbnail container into view
      scrollToThumbnails() {
          this.$refs.thumbnailContainer.scrollIntoView({
          behavior: "smooth",
          block: "end", // Align to the center of the viewport
          });
      },
      // Update main display (image or video)
      updateMainDisplay(item) {
        this.selectedItem = item;
      },
      // Generate thumbnail URL
      itemThumbnailUrl(item) {
        if (item.type === "image") {
          return item.small;
        } else if (item.type === "video") {

          const videoID = item.id;

          return `https://img.youtube.com/vi/${videoID}/hqdefault.jpg`;
        }
        return "";
      },
      // Flickity Navigation
      thumbnailPrevious() {
        this.$refs.flickityThumbnail.previous();
      },
      thumbnailNext() {
        this.$refs.flickityThumbnail.next();
      },
    },
  };
  </script>
  
<style lang="scss">

  @import "~styles/definitions";

  // Vars.
  $thumbnail_mobile_height: 5rem;
  $thumbnail_mobile_width: 7.8rem;
  $thumbnail_desktop_height: 10rem;
  $thumbnail_desktop_width: 15.6rem;

  $nav_button_mobile_size: 2rem;
  $nav_button_desktop_size: 3rem;

  // General styles
  .image-slider {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  // Main display
  .image-slider__main-display {
    width: 100%;
    margin-bottom: 1rem;
    position: relative;
    img {
      max-height: 51rem;
    }
  }

  .image-slider__main-image {
    width: 100%;
    height: auto;
    object-fit: contain;
    box-shadow: none;
  }

  .image-slider__main-video {
    position: relative;
    width: 100%;
    // Aspect ratio 16:9
    padding-top: 56.25%;
  }

  .image-slider__video-iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  // Thumbnails
  .image-slider__thumbnail-wrap {
    position: relative;
    width: 100%;
    margin: 0;
    overflow: hidden;
  }

  .image-slider__thumbnail-cell {
    margin: 1rem;

    &:first-child {
      padding-left: 3rem;
    }

    &:last-child {
      padding-right: r(48);
    }
  }

  .image-slider__thumbnail-button {
    display: flex;
    // Positions icons to center.
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;

    // Size related -- mobile.
    height: $thumbnail_mobile_height;
    width: $thumbnail_mobile_width;

    @include media-breakpoint-up (md) {

      // Size related -- desktop.
      height: $thumbnail_desktop_height;
      width: $thumbnail_desktop_width;
    }

    &:hover,
    &:focus {
      outline: .5rem solid $color-green-2025;
    }
  }

  .image-slider__thumbnail-cell.active-item {
    .image-slider__thumbnail-button {
      outline: .5rem solid $color-green-2025;
    }
  }

  .image-slider__thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  // Play Icon
  .image-slider__thumbnail-icon {
    position: absolute;
    color: $card-grid__background;
  }

  // Navigation Buttons
  .image-slider__nav-button {
    display: flex;
    align-items: center;
    justify-content: center;

    position: absolute;
    margin: 0 r(10);
    cursor: pointer;
    z-index: 10;

    // Colors
    background-color: $color-green-2025;
    border: solid $card-grid__background .25rem;
    border-radius: 50%;
    color: $card-grid__background;

    // Size related -- desktop.
    width: $nav_button_mobile_size;
    height: $nav_button_mobile_size;
    top: calc( 50% - #{$nav_button_mobile_size}/2 );

    @include media-breakpoint-up(md) {
      margin: 0 r(10);

      // Size related -- mobile.
      width: $nav_button_desktop_size;
      height: $nav_button_desktop_size;
      top: calc( 50% - #{$nav_button_desktop_size}/2 );
    }

    &:focus {
      background-color: $white;
      border: solid $color-green-2025 .25rem;
      color: $color-green-2025;
    }
  }

  .image-slider__nav-button--left {
    left: .5rem;

    // Flip the left chevron horizontally.
    .icon > svg {
      transform: scaleX(-1) !important;
      transform-origin: center;
    }
  }

  .image-slider__nav-button--right {
    right: .5rem;
  }
</style>
